/**
 * translationJobs — the TranslationJobProvider component (client-side background
 * translation jobs). The context, hook, and types live in
 * translationJobsContext.ts so this file exports ONLY a component (required for
 * Vite Fast Refresh / HMR).
 *
 * Runs translations in the browser (reusing the Anthropic proxy), mounted at app
 * root so a job survives in-app navigation (a full tab close/refresh ends it).
 * It owns the job STATE and PRODUCES generic notifications via NotificationProvider.
 */
import { useState, useRef, useCallback, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { translateCourse, friendlyTranslateError, type TranslateStep } from './translate'
import { useTenantWithUser } from './tenant'
import { getFlag, getLanguage } from './languages'
import { useNotifications } from './notifications'
import { FEATURE_ORGANISATIONS } from './featureFlags'
import { orgApi } from './orgApi'
import { TranslationJobsContext, type TranslationJob, type TranslationJobsCtx } from './translationJobsContext'

/** Cap simultaneous translations so TTS/API rate limits aren't swamped (each
 *  job runs many sequential TTS calls; 2 jobs keeps peak request rate sane). */
const MAX_CONCURRENT = 2

const STEP_LABEL: Record<TranslateStep, string> = {
  reading: 'Reading course structure…',
  content: 'Translating slide content…',
  preserving: 'Translating slide content…',
  narration: 'Generating narration scripts…',
  saving: 'Saving new course to library…',
  voiceover: 'Generating voiceover…',
  done: 'Done',
}

const headline = (source: string, target: string) =>
  `${getFlag(source)} → ${getFlag(target)} ${getLanguage(target).label}`

export function TranslationJobProvider({ children }: { children: ReactNode }) {
  const { userId } = useTenantWithUser()
  const navigate = useNavigate()
  const { notify, update, remove, toast, refresh } = useNotifications()
  const [jobs, setJobs] = useState<TranslationJob[]>([])
  const controllers = useRef<Map<string, AbortController>>(new Map())

  const patch = useCallback((id: string, p: Partial<TranslationJob>) => {
    setJobs(prev => prev.map(j => (j.id === id ? { ...j, ...p } : j)))
  }, [])

  const cancelJob = useCallback((id: string) => {
    controllers.current.get(id)?.abort()
  }, [])

  const startJob = useCallback<TranslationJobsCtx['startJob']>(({ courseId, title, source, target }) => {
    if (!userId) { console.error('[translate] cannot start — not signed in'); return { refused: 'not-signed-in' } }
    // Never run the same course→language twice concurrently.
    if (jobs.some(j => j.status === 'running' && j.courseId === courseId && j.target === target)) {
      return { refused: 'duplicate' }
    }
    // Global concurrency cap.
    if (jobs.filter(j => j.status === 'running').length >= MAX_CONCURRENT) {
      return { refused: 'at-capacity' }
    }

    const id = `tj-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`
    const ctrl = new AbortController()
    controllers.current.set(id, ctrl)
    const head = headline(source, target)

    setJobs(prev => [
      ...prev,
      { id, courseId, title, source, target, step: 'reading', progress: 0, status: 'running' },
    ])
    // Produce the notification + a "started" toast.
    notify({
      id, category: 'translation', status: 'progress', title: head, body: title, progress: 0,
      actions: [{ label: 'Cancel', variant: 'danger', onClick: () => cancelJob(id) }],
    })
    toast({ status: 'progress', title: head, body: `Translation started · ${title}` })

    translateCourse(
      courseId, target, userId,
      p => { patch(id, p); update(id, { progress: p.progress, body: STEP_LABEL[p.step] }) },
      ctrl.signal,
    )
      .then(newCourse => {
        patch(id, { status: 'done', progress: 100, step: 'done', newCourseId: newCourse.id })
        const open = () => navigate(`/editor/${newCourse.id}`)
        update(id, {
          status: 'success', progress: 100, body: `Ready · ${title}`,
          // Re-flag unread so the bell badge bumps for the completion event,
          // even if the user opened the bell (clearing it) during progress.
          read: false,
          actions: [{ label: 'Open translated course', variant: 'primary', onClick: open }],
        })
        toast({
          status: 'success', title: head, body: `Ready · ${title}`,
          actions: [{ label: 'Open translated course', variant: 'primary', onClick: open }],
        })
        // Also on the server (13 Oct 2026), so it is in the bell on every device: once it is saved
        // there, the in-browser copy gives way to it (clicking it opens the translated course).
        if (FEATURE_ORGANISATIONS) {
          orgApi.notificationCreate({ kind: 'translation_done', title: head, body: `Ready · ${title}`, link: `/editor/${newCourse.id}` })
            .then(() => { remove(id); refresh() }).catch(() => { /* the in-browser one stays */ })
        }
      })
      .catch(err => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          patch(id, { status: 'cancelled' })
          remove(id)
        } else {
          const raw = err instanceof Error ? err.message : String(err)
          const friendly = friendlyTranslateError(err)
          console.error(`[translate] job failed (${source}→${target}, "${title}"):`, raw, err)
          patch(id, { status: 'error', error: friendly })
          update(id, { status: 'error', body: friendly, read: false, actions: [] })
          toast({ status: 'error', title: head, body: friendly })
        }
      })
      .finally(() => controllers.current.delete(id))

    return { id }
  }, [userId, patch, cancelJob, jobs, notify, update, remove, toast, refresh, navigate])

  const dismissJob = useCallback((id: string) => {
    setJobs(prev => prev.filter(j => j.id !== id))
  }, [])

  const activeTargetsFor = useCallback((courseId: string) => {
    return jobs
      .filter(j => j.courseId === courseId && (j.status === 'running' || j.status === 'done'))
      .map(j => j.target)
  }, [jobs])

  const runningTargetsFor = useCallback((courseId: string) => {
    return jobs
      .filter(j => j.courseId === courseId && j.status === 'running')
      .map(j => j.target)
  }, [jobs])

  const runningCount = jobs.filter(j => j.status === 'running').length

  return (
    <TranslationJobsContext.Provider value={{
      jobs, startJob, cancelJob, dismissJob, activeTargetsFor,
      runningTargetsFor, runningCount, maxConcurrent: MAX_CONCURRENT,
    }}>
      {children}
    </TranslationJobsContext.Provider>
  )
}
